import { ElementProps, Group, Text, NumberInput, ComboboxProps } from '@mantine/core';
import styles from './RangeFilterButton.module.css';
import baseStyles from './BaseFilterButton.module.css';
import inputStyles from '../styles/Input.module.css';
import { BaseFilterButton, useBaseFilterButton } from './BaseFilterButton';

export interface RangeValue {
    min?: number;
    max?: number;
}

export type RangeFilterButtonProps = {
    label: string;
    disabled?: boolean;
    value?: RangeValue;
    defaultValue?: RangeValue;
    submittedValue?: RangeValue;
    onChange?: (value: RangeValue) => void;
    onSubmit?: (value: RangeValue) => void;
    fromLabel?: string;
    toLabel?: string;
    minPlaceholder?: string;
    maxPlaceholder?: string;
    showSubmitButtons?: boolean;
    decimalScale?: number;
    prefix?: string;
    suffix?: string;
    size?: string;
    form?: string;
    comboboxProps?: Partial<ComboboxProps>;
} & ElementProps<'div', 'onChange'>;

export const RangeFilterButton = ({
    label,
    disabled,
    value,
    defaultValue,
    submittedValue,
    onChange,
    onSubmit,
    form,
    size,
    fromLabel = 'From',
    toLabel = 'To',
    minPlaceholder = '',
    maxPlaceholder = '',
    showSubmitButtons = false,
    decimalScale = 0,
    prefix,
    suffix,
    comboboxProps,
    ...props
}: RangeFilterButtonProps) => {
    const hooks = useBaseFilterButton<RangeValue>({
        value,
        defaultValue,
        submittedValue,
        onChange,
        onSubmit,
        finalValue: {},
    });

    const { value: _value, setValue, submittedValue: _submittedValue } = hooks;

    const hasActiveFilters = _submittedValue.min !== undefined || _submittedValue.max !== undefined;

    /**
     * Parse a string or number value to a number or undefined
     */
    const parseNumericValue = (value: string | number): number | undefined => {
        if (typeof value === 'number') {
            return isNaN(value) ? undefined : value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') {
                return undefined;
            }

            const parsed = parseFloat(trimmed);
            return isNaN(parsed) ? undefined : parsed;
        }

        return undefined;
    };

    const handleMinChange = (val: string | number) => {
        const processedValue = parseNumericValue(val);
        setValue({ ..._value, min: processedValue });
    };

    const handleMaxChange = (val: string | number) => {
        const processedValue = parseNumericValue(val);
        setValue({ ..._value, max: processedValue });
    };

    const hiddenInputs = (
        <>
            <input
                type="hidden"
                name={form ? `${form}-min` : undefined}
                value={_submittedValue.min ?? ''}
                disabled={disabled}
            />
            <input
                type="hidden"
                name={form ? `${form}-max` : undefined}
                value={_submittedValue.max ?? ''}
                disabled={disabled}
            />
        </>
    );

    return (
        <BaseFilterButton
            label={label}
            disabled={disabled}
            size={size}
            showSubmitButtons={showSubmitButtons}
            comboboxProps={comboboxProps}
            hasActiveFilters={hasActiveFilters}
            dropdownClassName={styles.dropdown}
            triggerClassName={styles.trigger}
            badgeClassName={baseStyles.dot}
            hooks={hooks}
            hiddenInputs={hiddenInputs}
            {...props}
        >
            <Group
                align="center"
                gap="sm"
                wrap="nowrap"
                justify="space-between"
                aria-label={`${label} inputs`}
            >
                <Text size="sm" fw={500} className={`${inputStyles.label} ${styles.label}`}>
                    {fromLabel}
                </Text>
                <NumberInput
                    placeholder={minPlaceholder}
                    value={_value.min ?? ''}
                    onChange={handleMinChange}
                    disabled={disabled}
                    size={size}
                    decimalScale={decimalScale}
                    prefix={prefix}
                    suffix={suffix}
                    hideControls
                    aria-label={`${fromLabel} value`}
                    className={styles.inputWrapper}
                    classNames={{
                        input: inputStyles.input,
                    }}
                />
            </Group>

            <Group align="center" gap="sm" wrap="nowrap" justify="space-between">
                <Text size="sm" fw={500} className={`${inputStyles.label} ${styles.label}`}>
                    {toLabel}
                </Text>
                <NumberInput
                    placeholder={maxPlaceholder}
                    value={_value.max ?? ''}
                    onChange={handleMaxChange}
                    disabled={disabled}
                    size={size}
                    decimalScale={decimalScale}
                    prefix={prefix}
                    suffix={suffix}
                    hideControls
                    aria-label={`${toLabel} value`}
                    className={styles.inputWrapper}
                    classNames={{
                        input: inputStyles.input,
                    }}
                />
            </Group>
        </BaseFilterButton>
    );
};
